import { supportsPassiveEvents } from 'detect-passive-events';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import tweenFunctions from 'tween-functions';
import { useIntl } from 'gatsby-plugin-react-intl';

import { StyledSVG } from './styles';

const ScrollUpButton = ({
  children,
  ContainerClassName = 'ScrollUpButton__Container',
  TransitionClassName = 'ScrollUpButton__Toggled',
  ShowAtPosition = 150,
  AnimationDuration = 500,
  EasingType = 'easeOutCubic',
  StopPosition = 0,
}) => {
  const [scrollUp, setScrollUp] = useState('');
  const intl = useIntl();

  const animation = {
    startPosition: 0,
    currentAnimationTime: 0,
    startTime: null,
  };

  let animationFrame = null;

  useEffect(() => {
    if (!animationFrame) {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      window.addEventListener(
        'wheel',
        () => stopScrollingFrame(animationFrame),
        supportsPassiveEvents ? { passive: true } : false,
      ); // Stop animation if user mouse wheels during animation.
      window.addEventListener(
        'touchstart',
        () => stopScrollingFrame(animationFrame),
        supportsPassiveEvents ? { passive: true } : false,
      ); // Stop animation if user touches the screen during animation.
    }
    return () => {
      // Remove all events, since component is no longer mounted.
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener(
        'wheel',
        () => stopScrollingFrame(animationFrame),
        false,
      );
      window.removeEventListener(
        'touchstart',
        () => stopScrollingFrame(animationFrame),
        false,
      );
    };
  }, [animationFrame, stopScrollingFrame]);

  const toggleScrollUp = className => setScrollUp(className);

  function handleScroll() {
    // window.scrollY = current scroll position
    // ShowAtPosition = position at which we want the button to show.

    if (window.scrollY > ShowAtPosition) {
      // styles.Toggled = the class name we want applied to transition the button in.
      toggleScrollUp(TransitionClassName);
    } else {
      // remove the class name
      toggleScrollUp('');
    }
  }

  function onClick() {
    // Scroll to StopPosition
    stopScrollingFrame(); // Stoping all AnimationFrames
    animation.startPosition = window.scrollY; // current scroll position
    animation.currentAnimationTime = 0;
    animation.startTime = null;
    // Start the scrolling animation.
    animationFrame = window.requestAnimationFrame(scrollingFrame);
  }

  function scrollingFrame() {
    const timestamp = Math.floor(Date.now());
    // If startTime has not been assigned a value, assign it the start timestamp.
    if (!animation.startTime) {
      animation.startTime = timestamp;
    }

    // set currentAnimationTime every iteration of ScrollingFrame()
    animation.currentAnimationTime = timestamp - animation.startTime;
    // if we hit the StopPosition, StopScrollingFrame()
    if (window.scrollY <= StopPosition) {
      stopScrollingFrame();
    } else {
      // Otherwise continue ScrollingFrame to the StopPosition.
      // Does not support horizontal ScrollingFrame.
      // Let TweenFunctions handle the math to give us a new position based on AnimationDuration and EasingType type
      let YPos = tweenFunctions[EasingType](
        animation.currentAnimationTime,
        animation.startPosition,
        StopPosition,
        AnimationDuration,
      );
      if (YPos <= StopPosition) {
        YPos = StopPosition;
      }
      window.scrollTo(0, YPos);
      // Request another frame to be painted
      animationFrame = window.requestAnimationFrame(scrollingFrame);
    }
  }

  function stopScrollingFrame(frame) {
    // Stop the animation Frames.
    const animationFrameId = frame || animationFrame;
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
    }
  }

  return children ? (
    <div
      aria-label={intl.formatMessage({ id: 'scrollBackUp' })}
      role="button"
      tabIndex={scrollUp ? 0 : -1}
      className={`${ContainerClassName}  ${scrollUp}`}
      onClick={onClick}
      onKeyPress={onClick}
    >
      {children}
    </div>
  ) : (
    <div
      aria-label={intl.formatMessage({ id: 'scrollBackUp' })}
      role="button"
      tabIndex={scrollUp ? 0 : -1}
      className={`${ContainerClassName}  ${scrollUp}`}
      onClick={onClick}
      onKeyPress={onClick}
    >
      <StyledSVG
        viewBox="0 0 32 32"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        x="0"
        y="0"
        xmlSpace="preserve"
      >
        <path
          transform="scale(1.4) translate(1,-5)"
          d="M19.196 23.429q0 0.232-0.179 0.411l-0.893 0.893q-0.179 0.179-0.411 0.179t-0.411-0.179l-7.018-7.018-7.018 7.018q-0.179 0.179-0.411 0.179t-0.411-0.179l-0.893-0.893q-0.179-0.179-0.179-0.411t0.179-0.411l8.321-8.321q0.179-0.179 0.411-0.179t0.411 0.179l8.321 8.321q0.179 0.179 0.179 0.411zM19.196 16.571q0 0.232-0.179 0.411l-0.893 0.893q-0.179 0.179-0.411 0.179t-0.411-0.179l-7.018-7.018-7.018 7.018q-0.179 0.179-0.411 0.179t-0.411-0.179l-0.893-0.893q-0.179-0.179-0.179-0.411t0.179-0.411l8.321-8.321q0.179-0.179 0.411-0.179t0.411 0.179l8.321 8.321q0.179 0.179 0.179 0.411z"
        />
      </StyledSVG>
    </div>
  );
};

ScrollUpButton.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  StopPosition: PropTypes.number,
  ShowAtPosition: PropTypes.number, // show button under this position,
  EasingType: PropTypes.oneOf([
    'linear',
    'easeInQuad',
    'easeOutQuad',
    'easeInOutQuad',
    'easeInCubic',
    'easeOutCubic',
    'easeInOutCubic',
    'easeInQuart',
    'easeOutQuart',
    'easeInOutQuart',
    'easeInQuint',
    'easeOutQuint',
    'easeInOutQuint',
    'easeInSine',
    'easeOutSine',
    'easeInOutSine',
    'easeInExpo',
    'easeOutExpo',
    'easeInOutExpo',
    'easeInCirc',
    'easeOutCirc',
    'easeInOutCirc',
    'easeInElastic',
    'easeOutElastic',
    'easeInOutElastic',
    'easeInBack',
    'easeOutBack',
    'easeInOutBack',
    'easeInBounce',
    'easeOutBounce',
    'easeInOutBounce',
  ]),
  AnimationDuration: PropTypes.number, // seconds
  ContainerClassName: PropTypes.string,
  TransitionClassName: PropTypes.string,
};

export default ScrollUpButton;
